/**
 * The bank-transfer payment provider, as the storefront sees it.
 *
 * Deliberately free of Stripe: `lib/payment.tsx` calls `loadStripe` at module
 * scope, and the query layer that reads these details has no business pulling
 * Stripe's script loader in behind it. `isBankTransfer` is re-exported from
 * there so it still sits beside `isStripe` / `isManual` when you go looking.
 */

export const isBankTransfer = (providerId?: string) => {
  return providerId?.startsWith("pp_bank-transfer");
};

/**
 * The marketplace's payee details, as the provider emits them on an initiated
 * payment session (`payment_collection.payment_sessions[].data`).
 *
 * These are SalonStocker's own bank details, not the salon's - nothing here is
 * customer PII, so none of it needs replay masking (ADR-0006).
 */
export interface BankTransferDetails {
  bankName: string;
  accountName: string;
  bsb: string;
  accountNumber: string;
  /**
   * Optional. `BANK_TRANSFER_PAYID` is deliberately not a required provider
   * option, and the backend omits the key entirely rather than sending null
   * when it is unset, so absence here means "not configured"
   * (salonstocker/api#204).
   */
  payid?: string;
}

/**
 * Whether a bank-transfer checkout has nothing to pay into, and so must not be
 * allowed to complete.
 *
 * `parseBankTransferDetails` returns null on a partial provider payload. Left
 * unchecked, the buyer completes an order they cannot settle, onto a
 * confirmation screen that has no details to show them either. Lives here
 * rather than inline in `PaymentStep` so it can be tested without standing up
 * that step's cart/provider/address/session mocks.
 *
 * Only meaningful once sessions exist: before that the details have not been
 * fetched, and the step is gated on its own loading state anyway.
 */
export function isBankTransferUnpayable({
  providerId,
  sessionCount,
  details
}: {
  providerId?: string | null;
  sessionCount: number;
  details: BankTransferDetails | null;
}): boolean {
  return (
    isBankTransfer(providerId ?? undefined) === true &&
    sessionCount > 0 &&
    !details
  );
}

/** A non-empty, trimmed string, or undefined. Blank config reads as absent. */
const nonEmpty = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;

/**
 * Reads the payee details off a payment session's `data`.
 *
 * Returns null when the payload is not a bank-transfer session - a Stripe
 * session reaches this same path as soon as the buyer changes provider - or
 * when it is missing any of the four details the provider always sends
 * together. A half-filled panel is worse than no panel.
 */
export function parseBankTransferDetails(
  data: unknown
): BankTransferDetails | null {
  if (!data || typeof data !== "object") return null;
  const raw = data as Record<string, unknown>;

  const bankName = nonEmpty(raw.bank_name);
  const accountName = nonEmpty(raw.account_name);
  const bsb = nonEmpty(raw.bsb);
  const accountNumber = nonEmpty(raw.account_number);

  if (!bankName || !accountName || !bsb || !accountNumber) return null;

  return {
    bankName,
    accountName,
    bsb,
    accountNumber,
    payid: nonEmpty(raw.payid)
  };
}
